import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ContractState } from '@received/pricing-model';
import { Button, Table, Icon, ImportItemsProgress, SearchBar, AssignSubscription, EmptyStateCard, Sidebars } from '../../../components';
import { PathsConfig, httpService } from '../../../services';
import { setSidebarIsOpen } from '../../../storeSlices/generalSlice';
import { Store } from '../../../types/storeTypes';
import { setOpenSuccessErrorModal } from '../../../storeSlices/errorSuccessSlice';
import { Sort, SortOrder } from '../../../types/generalTypes';
import { useDebounce } from '../../../hooks';
import { pageOptions, subscribersTableHeader } from './Subscriptions.utils';
import { SubscribersView } from '../../../types/subscriptionTypes';
import { SidebarType } from '../../../components/Sidebars/Sidebars.utils';
import styles from './Subscriptions.module.scss';

export interface SubscribersResponse {
	viewList: SubscribersView[];
	totalCount: number;
}

const defaultNumberOfItems = '100';

export const Subscriptions = () => {
	const { t } = useTranslation('translation');
	const { isSidebarOpen, toastNotification } = useSelector((store: Store) => store.general);

	const [subscribersList, setSubscribersList] = useState<SubscribersView[]>([]);
	const [sort, setSort] = useState<Sort>({ orderBy: 'name', order: SortOrder.ASC });
	const [searchText, setSearchText] = useState('');
	const [totalCount, setTotalCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(0);
	const [numberOfItems, setNumberOfItems] = useState(defaultNumberOfItems);
	const [showAssignSubscription, setShowAssignSubscription] = useState(false);
	const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
	const [tableSmallRowsView, setTableSmallRowsView] = useState(false);
	const [openSideBarById, setOpenSideBarById] = useState<{ sideBarType: SidebarType; id: string }>();
	const [states, setStates] = useState<ContractState[]>([ContractState.ACTIVE]);

	const dispatch = useDispatch();

	useEffect(() => {
		!isSidebarOpen && dispatch(setSidebarIsOpen(true));
	}, []);

	useEffect(() => {
		setTableSmallRowsView(+numberOfItems >= 50);
	}, [numberOfItems]);

	useEffect(() => {
		getSubscribersData();
	}, [numberOfItems, pageNumber, toastNotification.showToastNotification, sort, states]);

	useEffect(() => {
		searchSubscribers();
	}, [searchText]);

	const searchSubscribers = () => {
		getSubscribersData();
		setPageNumber(0);
	};

	const getSubscribersData = async (page?: number, items?: string) => {
		try {
			const res: SubscribersResponse = (
				await httpService({
					dispatch,
					path: PathsConfig.getSubscriptionsViewData,
					params: {
						pageSize: parseInt(items || numberOfItems),
						pageNumber: page || pageNumber,
						orderBy: sort.orderBy,
						textFilter: searchText || undefined,
						order: sort.order,
						states: states.length ? states : undefined,
					},
				})
			).data;

			setTotalCount(res?.totalCount);
			setSubscribersList(res.viewList);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	const onRefreshData = () => {
		getSubscribersData();
		setNumberOfItems(defaultNumberOfItems);
		setPageNumber(0);
	};

	const onSearch = useDebounce(setSearchText, 650);

	return (
		<div className={styles.globalPageContainer}>
			<div className={styles.container}>
				<ImportItemsProgress onRefresh={() => onRefreshData()} />
				<Sidebars openSideBarById={openSideBarById} onCloseSideBars={() => setOpenSideBarById(undefined)} onDataChange={onRefreshData} />

				<div className={styles.header}>
					<header className={styles.customHeader}>
						<h1 data-testid='subscriptions-title'>{t('Subscriptions')}</h1>

						<div className={styles.buttonsContainer}>
							<SearchBar placeholder={t('search')} onChange={onSearch} className={styles.search} />
							<Button onClick={() => setShowAssignSubscription(true)} className={styles.assignSubscription}>
								{t('assignSubscription')}
								<Icon imgType='add' color='white' width={1.2} className={styles.addIcon} />
							</Button>
						</div>
					</header>
				</div>

				<Table
					sort={sort}
					numberOfItems={numberOfItems}
					header={subscribersTableHeader(tableSmallRowsView, t)}
					rows={subscribersList}
					className={styles.tableContainer}
					numberOfPages={Math.ceil(totalCount / +numberOfItems)}
					pageItemsOptions={pageOptions(t)}
					totalNumberOfItems={totalCount}
					tableRowClassName={tableSmallRowsView ? styles.extendedTableRowClassName : styles.tableRowClassName}
					onUpdateNumberOfItems={setNumberOfItems}
					onUpdatePageNumber={setPageNumber}
					showSwitch
					defaultSwitchChecked={false}
					onToggleSwitch={(showDraft: boolean) => setStates(showDraft ? [] : [ContractState.ACTIVE])}
					onRowPress={(data: SubscribersView) =>
						data.contractId &&
						setOpenSideBarById({
							sideBarType: SidebarType.CONTRACT,
							id: data.contractId,
						})
					}
					onFlipOrder={(accessor) => setSort({ orderBy: accessor, order: sort.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC })}
					emptyState={
						!searchText ? (
							<EmptyStateCard showIcon imgType='refresh' title={t('noAssignedSubscriptions')}>
								<Button className={styles.createButton} onClick={() => setShowAssignSubscription(true)}>
									<span>{t('assignSubscription')}</span>
									<Icon imgType='add' color='white' width={1.2} className={styles.addIcon} />
								</Button>
							</EmptyStateCard>
						) : undefined
					}
				/>

				<AssignSubscription
					selectedSubscriptionId={selectedSubscriptionId}
					isOpen={showAssignSubscription}
					onAssignSubscription={() => getSubscribersData()}
					closeSideBar={() => {
						setShowAssignSubscription(false);
						setSelectedSubscriptionId('');
					}}
				/>
			</div>
		</div>
	);
};
